// Musteri aktivite log sayfasi — mobile/QR'dan gelen her API cagrisi listelenir.
// Filtre: musteri (secici modal), endpoint (icerir), tarih araligi.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Filter, Search, Smartphone, UserSearch, X } from 'lucide-react';
import {
  Buton,
  BosDurum,
  Girdi,
  Rozet,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  type ColumnDef,
  type RozetTonu,
  type TarihAraligi,
} from '@emanetx/ui';
import { aktiviteKayitlariniListele, type AktiviteKaydi, type Musteri } from '@emanetx/sdk';
import { istemci } from '../istemci.js';
import {
  MusteriSeciciDialog,
  platformTon,
  yasHesapla,
} from '../musteriler/musteri-secici-dialog.js';

function son24Saat(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setHours(s.getHours() - 24);
  return { baslangic: s, bitis: e };
}

function durumTon(d: number): RozetTonu {
  if (d >= 500) return 'kirmizi';
  if (d >= 400) return 'turuncu';
  if (d >= 300) return 'mavi';
  return 'yesil';
}

function yontemTon(y: string): RozetTonu {
  switch (y) {
    case 'POST':
      return 'mavi';
    case 'PUT':
    case 'PATCH':
      return 'turuncu';
    case 'DELETE':
      return 'kirmizi';
    default:
      return 'fume';
  }
}

export function SayfaAktivite() {
  const [sayfa, setSayfa] = useState(1);
  const [aralik, setAralik] = useState<TarihAraligi>(son24Saat);
  const [seciliMusteri, setSeciliMusteri] = useState<Musteri | null>(null);
  const [endpoint, setEndpoint] = useState('');
  const [seciciAcik, setSeciciAcik] = useState(false);

  const sorgu = {
    sayfa,
    boyut: 100,
    ...(seciliMusteri && { musteriId: seciliMusteri.id }),
    ...(endpoint && { endpoint }),
    ...(tarihIso(aralik.baslangic) && { baslangic: tarihIso(aralik.baslangic) }),
    ...(tarihIso(aralik.bitis) && { bitis: tarihIso(aralik.bitis) }),
  };

  const liste = useQuery({
    queryKey: ['aktivite', sorgu],
    queryFn: () => aktiviteKayitlariniListele(istemci, sorgu),
    placeholderData: (o) => o,
    refetchInterval: 15_000,
  });

  const kolonlar = useMemo<ColumnDef<AktiviteKaydi, unknown>[]>(
    () => [
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Zaman',
        meta: { etiket: 'Zaman' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.olusturmaTarihi).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        ),
      },
      {
        accessorKey: 'musteriAdSoyad',
        header: 'Müşteri',
        meta: { etiket: 'Müşteri' },
        cell: ({ row }) => {
          if (!row.original.musteriId) {
            return <span className="text-fume-400 text-xs">anonim</span>;
          }
          return (
            <div className="flex flex-col">
              <span className="text-fume-900 dark:text-fume-50 text-xs font-medium">
                {row.original.musteriAdSoyad ?? '—'}
              </span>
              <span className="text-fume-500 font-mono text-[10px]">
                {row.original.musteriTelefon ?? row.original.musteriId.slice(0, 10) + '…'}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'yontem',
        header: 'Yöntem',
        meta: { etiket: 'Yöntem' },
        cell: ({ row }) => (
          <Rozet ton={yontemTon(row.original.yontem)} className="font-mono text-[10px]">
            {row.original.yontem}
          </Rozet>
        ),
      },
      {
        accessorKey: 'endpoint',
        header: 'Endpoint',
        meta: { etiket: 'Endpoint' },
        cell: ({ row }) => (
          <code className="text-fume-700 dark:text-fume-200 font-mono text-xs">
            {row.original.endpoint}
          </code>
        ),
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={durumTon(row.original.durum)} className="font-mono text-[10px]">
            {row.original.durum}
          </Rozet>
        ),
      },
      {
        accessorKey: 'sureMs',
        header: 'Süre',
        meta: { etiket: 'Süre' },
        cell: ({ row }) => {
          const ms = row.original.sureMs;
          const renk = ms > 1000 ? 'text-red-600' : ms > 300 ? 'text-amber-600' : 'text-fume-500';
          return <span className={`font-mono text-xs ${renk}`}>{ms}ms</span>;
        },
      },
      {
        accessorKey: 'ipAdresi',
        header: 'IP',
        meta: { etiket: 'IP' },
        cell: ({ row }) => (
          <span className="text-fume-400 font-mono text-[11px]">
            {row.original.ipAdresi ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'userAgent',
        header: 'User-Agent',
        meta: { etiket: 'User-Agent' },
        cell: ({ row }) => (
          <span
            className="text-fume-400 block max-w-[260px] truncate text-[11px]"
            title={row.original.userAgent ?? ''}
          >
            {row.original.userAgent ?? '—'}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Müşteri Aktivitesi</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Mobil ve QR üzerinden gelen tüm API çağrılarının canlı log'u. 15 saniyede bir tazelenir.
        </p>
      </header>

      {seciliMusteri && (
        <SeciliMusteriKarti m={seciliMusteri} onTemizle={() => setSeciliMusteri(null)} />
      )}

      <VeriTablo
        veri={liste.data?.ogeler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={100}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        varsayilanGizliKolonlar={{ ipAdresi: false, userAgent: false }}
        bosDurum={
          <BosDurum
            ikon={Activity}
            baslik="Bu kriterlerle kayıt yok"
            aciklama="Tarih veya filtreyi değiştirin."
          />
        }
        ustAksiyonlar={
          <>
            <TarihAraligiSecici
              deger={aralik}
              onDegisim={(a) => {
                setAralik(a);
                setSayfa(1);
              }}
              etiketYok
              className="h-10 text-xs"
            />
            <Filter size={13} className="text-fume-400" />
            <Buton boyut="kucuk" stil="cizgili" onClick={() => setSeciciAcik(true)}>
              <UserSearch size={13} />
              {seciliMusteri ? 'Müşteri Değiştir' : 'Müşteri Seç'}
            </Buton>
            <div className="relative">
              <Search
                size={13}
                className="text-fume-400 pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="endpoint (/musteri/ben)"
                value={endpoint}
                onChange={(e) => {
                  setEndpoint(e.target.value);
                  setSayfa(1);
                }}
                className="h-10 w-52 pl-8 font-mono text-xs"
              />
            </div>
          </>
        }
      />

      <MusteriSeciciDialog
        acik={seciciAcik}
        onDegisim={setSeciciAcik}
        onSec={(m) => {
          setSeciliMusteri(m);
          setSayfa(1);
        }}
      />
    </div>
  );
}

function SeciliMusteriKarti({ m, onTemizle }: { m: Musteri; onTemizle: () => void }) {
  const yas = yasHesapla(m.dogumTarihi);
  return (
    <div className="border-marka-200 bg-marka-50 dark:border-marka-500/30 dark:bg-marka-500/10 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border px-4 py-3">
      <div className="flex flex-col">
        <span className="text-fume-500 dark:text-fume-400 text-[10px] font-semibold uppercase">
          Müşteri
        </span>
        <span className="text-fume-900 dark:text-fume-50 text-sm font-semibold">{m.adSoyad}</span>
      </div>
      <Bilgi etiket="Telefon" deger={m.telefon} mono />
      <Bilgi etiket="ID" deger={m.id.slice(0, 12) + '…'} mono kucuk />
      <Bilgi etiket="Dil" deger={m.dilTercihi.toUpperCase()} />
      {m.dogumTarihi && (
        <Bilgi
          etiket="Doğum / Yaş"
          deger={`${new Date(m.dogumTarihi).toLocaleDateString('tr-TR')}${yas !== null ? ` (${yas})` : ''}`}
        />
      )}
      {m.cihaz && (
        <div className="flex flex-col">
          <span className="text-fume-500 dark:text-fume-400 text-[10px] font-semibold uppercase">
            Cihaz
          </span>
          <span className="text-fume-900 dark:text-fume-50 inline-flex items-center gap-1.5 text-xs">
            <Smartphone size={12} className="text-fume-400" />
            <Rozet ton={platformTon(m.cihaz.platform)} className="text-[10px]">
              {m.cihaz.platform}
            </Rozet>
            {m.cihaz.cihazAdi && <span>{m.cihaz.cihazAdi}</span>}
            {m.cihaz.uygulamaSurum && (
              <span className="text-fume-500 font-mono">v{m.cihaz.uygulamaSurum}</span>
            )}
          </span>
        </div>
      )}
      <button
        onClick={onTemizle}
        className="text-fume-500 hover:text-fume-900 dark:hover:text-fume-50 ml-auto inline-flex items-center gap-1 text-xs"
        aria-label="Filtreyi temizle"
      >
        <X size={13} /> Temizle
      </button>
    </div>
  );
}

function Bilgi({
  etiket,
  deger,
  mono,
  kucuk,
}: {
  etiket: string;
  deger: string;
  mono?: boolean;
  kucuk?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-fume-500 dark:text-fume-400 text-[10px] font-semibold uppercase">
        {etiket}
      </span>
      <span
        className={`text-fume-900 dark:text-fume-50 ${kucuk ? 'text-[11px]' : 'text-xs'} ${mono ? 'font-mono' : ''}`}
      >
        {deger}
      </span>
    </div>
  );
}
